import { useMemo } from 'react'
import { useEntriesStore, useSettingsStore } from '@/core/storage/zustand-store'

export interface AutocompleteLists {
  pagadores: string[]
  categorias: string[]
  tags: string[]
}

/**
 * Returns the deduplicated, sorted lists of values the user has already
 * used. Combines the current entries with the cached lists kept in
 * Settings (which survive entry deletions so a one-off pagador name stays
 * suggestable next year).
 *
 * Each known* list is selected individually so Zustand's Object.is
 * comparison stays cheap and we never trigger the new-object-every-render
 * loop.
 */
export function useAutocomplete(): AutocompleteLists {
  const entries = useEntriesStore((s) => s.entries)
  const knownPagadores = useSettingsStore((s) => s.settings.knownPagadores)
  const knownCategorias = useSettingsStore((s) => s.settings.knownCategorias)
  const knownTags = useSettingsStore((s) => s.settings.knownTags)

  return useMemo(() => {
    const pagadores = new Set<string>()
    const categorias = new Set<string>()
    const tags = new Set<string>()

    for (const value of knownPagadores) addIfNonEmpty(pagadores, value)
    for (const value of knownCategorias) addIfNonEmpty(categorias, value)
    for (const value of knownTags) addIfNonEmpty(tags, value)

    for (const entry of entries) {
      addIfNonEmpty(pagadores, entry.pagador)
      addIfNonEmpty(categorias, entry.categoria)
      addIfNonEmpty(tags, entry.tag)
    }

    return {
      pagadores: sorted(pagadores),
      categorias: sorted(categorias),
      tags: sorted(tags),
    }
  }, [entries, knownPagadores, knownCategorias, knownTags])
}

function addIfNonEmpty(set: Set<string>, value: string | undefined): void {
  if (typeof value === 'string' && value.trim().length > 0) set.add(value.trim())
}

function sorted(values: Set<string>): string[] {
  return Array.from(values).sort((a, b) => a.localeCompare(b, 'pt-BR'))
}
